// src/services/authService.js
import axios from 'axios';
import config from '../config';
import { toApiError } from './api';

class AuthService {
  constructor() {
    this.api = axios.create({
      baseURL: config.API_BASE_URL,
      headers: {
        'Content-Type': 'application/json'
      }
    });
  }

  async login(email, password) {
    try {
      const response = await this.api.post('/auth/login', {
        email: (email || '').trim(),
        password
      });

      this.setAuthData(response.data);
      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      if (apiError.status === 401) {
        apiError.message = 'Nesprávný e-mail nebo heslo.';
      }
      throw apiError;
    }
  }

  async verifyToken() {
    try {
      const token = this.getToken();
      if (!token) return null;

      this.setAuthHeader(token);
      const response = await this.api.get('/auth/verify');
      return response.data;
    } catch (error) {
      // Only clear auth on 401 (invalid/expired token)
      // Keep user logged in on network errors or 5xx (tunnel hiccups)
      if (error.response && error.response.status === 401) {
        this.clearAuthData();
      }
      return null;
    }
  }

  logout() {
    this.clearAuthData();
  }

  // Users management (admin only)
  async getUsers() {
    try {
      const response = await this.api.get('/auth/users');
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  async createUser(userData) {
    try {
      const response = await this.api.post('/auth/users', userData);
      return response.data;
    } catch (error) {
      throw toApiError(error);
    }
  }

  async deleteUser(userId) {
    try {
      await this.api.delete(`/auth/users/${userId}`);
    } catch (error) {
      throw toApiError(error);
    }
  }

  // Auth data management
  setAuthData(authData) {
    const { token, user } = authData;
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    this.setAuthHeader(token);
  }

  clearAuthData() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete this.api.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['Authorization'];
  }

  setAuthHeader(token) {
    this.api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    // Also set it for the global axios instance for other services
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  getCurrentUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  getToken() {
    return localStorage.getItem('token');
  }
}

const authService = new AuthService();
export default authService;